import { gql, useQuery, useMutation } from "@apollo/client";
import { useState, useEffect } from "react";
import { useController, UseControllerProps, useForm } from "react-hook-form";
import { View } from "react-native";
import { Text } from "react-native-paper";

import {
  ActionsContainer,
  Button,
  ScreenContainer,
  TextInput,
  ScrollableContainer,
  KeyboardAvoidingView,
} from "../../components";
import { ScreenProps } from "./types";
import { QUERY, Query, InitiateMutation, INITIATE_MUTATION, PENDING_EMAIL_CHANGE_FRAGMENT } from "./utils";
import { GraphQLError } from "graphql";
import {
  SessionExpired,
  Failed,
  AttemptsExhausted,
  CodeSentInterrupt,
  Updating,
  CancelInterrupt,
  DuplicateEmail,
} from "./modals";
import { useCurrentUser, controlModals } from "../../hooks";

interface FormData {
  code: string;
}

const rules = {
  pattern: { value: /^\d{6}$/, message: "must be six digits" },
  required: "required",
};

function CodeInput({ control, name }: UseControllerProps<FormData>) {
  const {
    field: { onChange, ...field },
    fieldState: { error },
  } = useController({ control, name, rules });

  return (
    <TextInput {...field} error={error} inputMode="numeric" maxLength={6} onChangeText={onChange} placeholder="Enter code" />
  );
}

enum ConfirmStatus {
  SUCCESS = "SUCCESS",
  NOT_FOUND = "NOT_FOUND",
  CODE_MISMATCH = "CODE_MISMATCH",
  ATTEMPTS_EXHAUSTED = "ATTEMPTS_EXHAUSTED",
  EXPIRED = "EXPIRED",
}

interface ConfirmMutation {
  confirmEmailChange: { status: ConfirmStatus };
}

export const CONFIRM_MUTATION = gql`
  mutation ($code: String!) {
    confirmEmailChange(code: $code) {
      status
      currentMember {
        ...PendingEmailChangeFragment
      }
    }
  }
  ${PENDING_EMAIL_CHANGE_FRAGMENT}
`;

interface ValidationError {
  message: string;
  title?: string;
}

export const ONELOGIN_DUPLICATE = "ONELOGIN_DUPLICATE";

const useModalState = controlModals(
  "attemptsExhausted",
  "cancelInterrupt",
  "codeSent",
  "failed",
  "updating",
  "expired",
  "duplicateEmail"
);

export default function EmailConfirmScreen({ navigation }: ScreenProps<"Confirm">) {
  const { data } = useQuery<Query>(QUERY);
  const [initiateChange, { loading: initiateLoading }] = useMutation<InitiateMutation>(INITIATE_MUTATION);
  const { email } = data?.currentMember?.pendingEmailChange || {};
  const [error, setError] = useState<ValidationError>({ message: "" });
  const modal = useModalState();
  const [confirmChange, { loading: confirmLoading }] = useMutation<ConfirmMutation>(CONFIRM_MUTATION, {
    errorPolicy: "all",
    context: {
      callerWillHandleError: (_: ReadonlyArray<GraphQLError>, networkError: Error) => {
        if (networkError) {
          modal.updating.dismiss();
          modal.failed.show();
        }
        return !networkError;
      },
    },
  });

  const { setAcknowledgedEmailChangeExpired } = useCurrentUser();

  function closeErrorMessage() {
    setError({ message: "" });
  }

  function navigateToAccountSettings() {
    navigation.getParent()?.navigate("AccountSettings");
  }

  const {
    control,
    handleSubmit,
    setFocus,
    formState: { isValid, isSubmitting },
  } = useForm<FormData>({ mode: "onBlur" });

  async function onSubmit({ code }: FormData) {
    modal.updating.show();
    const { data, errors } = await confirmChange({ variables: { code } });
    modal.updating.dismiss();
    if (errors?.length || !data) {
      const isDuplicate = errors?.find((e) => e.message === ONELOGIN_DUPLICATE);
      if (isDuplicate) {
        // irrecoverable
        modal.duplicateEmail.show();
      } else {
        // irrecoverable
        modal.failed.show();
      }
    } else {
      const { status } = data.confirmEmailChange;
      switch (status) {
        case ConfirmStatus.SUCCESS:
          navigation.replace("Success");
          break;
        case ConfirmStatus.CODE_MISMATCH:
          setError({ message: "The code entered is invalid.", title: "Invalid Code" });
          break;
        case ConfirmStatus.ATTEMPTS_EXHAUSTED:
          modal.attemptsExhausted.show();
          break;
        case ConfirmStatus.EXPIRED:
          modal.expired.show();
          break;
      }
    }
  }

  async function onResend() {
    closeErrorMessage();
    const result = await initiateChange({ variables: { email } });
    const { message = "", success } = result?.data?.initiateEmailChange || {};

    if (success) modal.codeSent.show();
    else setError({ message });
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setFocus("code");
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [setFocus]);

  return (
    <ScreenContainer testID="ConfirmScreen">
      <AttemptsExhausted
        visible={modal.attemptsExhausted.visible}
        onDismiss={() => {
          modal.attemptsExhausted.dismiss();
          navigateToAccountSettings();
        }}
      />
      <CodeSentInterrupt visible={modal.codeSent.visible} email={email} onDismiss={modal.codeSent.dismiss} />
      <CancelInterrupt
        visible={modal.cancelInterrupt.visible}
        onCancel={() => {
          modal.cancelInterrupt.dismiss();
          navigateToAccountSettings();
        }}
        onDismiss={modal.cancelInterrupt.dismiss}
      />
      <Updating visible={modal.updating.visible} />
      <Failed
        visible={modal.failed.visible}
        onDismiss={() => {
          modal.failed.dismiss();
          navigateToAccountSettings();
        }}
      />
      <DuplicateEmail
        visible={modal.duplicateEmail.visible}
        email={email!}
        onDismiss={() => {
          modal.duplicateEmail.dismiss();
          navigateToAccountSettings();
        }}
      />
      <SessionExpired
        visible={modal.expired.visible}
        onDismiss={() => {
          setAcknowledgedEmailChangeExpired(true);
          modal.expired.dismiss();
          navigateToAccountSettings();
        }}
      />
      <KeyboardAvoidingView>
        <ScrollableContainer>
          <View className="p-6">
            <Text className="text-title-5 font-700 mb-4">Verification email sent</Text>
            <Text className="text-regular-normal">A verification code has been sent to your new email:</Text>
            <Text className="text-regular-normal font-700 mb-8">{email}</Text>
            <Text className="text-regular-normal">Enter the code to continue:</Text>
            <CodeInput control={control} name="code" />

            {email && (
              <View className="mt-6">
                <Button onPress={onResend}>Resend code</Button>
              </View>
            )}
          </View>
        </ScrollableContainer>
        <ActionsContainer errorMessage={error.message} errorTitle={error.title} onCloseErrorMessage={closeErrorMessage}>
          <Button
            onPress={handleSubmit(onSubmit)}
            mode="contained"
            loading={confirmLoading || initiateLoading}
            isValid={isValid}
            disabled={isSubmitting}
          >
            Continue
          </Button>
          <Button onPress={modal.cancelInterrupt.show}>Cancel</Button>
        </ActionsContainer>
      </KeyboardAvoidingView>
    </ScreenContainer>
  );
}
